import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "../lib/api";

type BusinessUser = {
  id: number;
  name: string;
  email: string;
  role: string;
  status: string;
  last_login_at?: string | null;
};

export default function ProfileUsers() {
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    apiGet<any>("/xapi/profile/users")
      .then((res) => {
        if (!alive) return;
        setPayload(res);
      })
      .catch((err: any) => {
        if (alive) setError(err?.message || "Could not load business users.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return <div className="section-box skeleton">Loading business users...</div>;
  }

  const users = (payload?.users ?? []) as BusinessUser[];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Business Users</h1>
          <p className="text-sm text-gray-500">
            Manage who can access this business profile.
          </p>
        </div>
        <Link to="/profile" className="btn btn-white">
          Back to profile
        </Link>
      </header>

      {error && <div className="alert-danger">{error}</div>}

      {!payload?.is_business && (
        <div className="alert-info">Business user management is available for business profiles.</div>
      )}

      {payload?.company && (
        <div className="card-box">
          <h2 className="text-lg font-semibold">{payload.company.name || "Business profile"}</h2>
          <div className="mt-2 grid gap-3 text-sm sm:grid-cols-2">
            <Info label="Registration number" value={payload.company.registration_number} />
            <Info label="Email" value={payload.company.email} />
            <Info label="Phone" value={payload.company.phone} />
          </div>
        </div>
      )}

      <div className="section-box overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="table-header">
            <tr>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Last login</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.map((user) => (
              <tr key={user.id}>
                <td className="px-4 py-3">
                  <div className="font-semibold">{user.name || user.email}</div>
                  <div className="text-xs text-gray-500">{user.email}</div>
                </td>
                <td className="px-4 py-3">{user.role}</td>
                <td className="px-4 py-3 capitalize">{user.status.replace(/_/g, " ")}</td>
                <td className="px-4 py-3 text-gray-500">
                  {user.last_login_at ? new Date(user.last_login_at).toLocaleString() : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {payload?.message && <div className="alert-info">{payload.message}</div>}
    </div>
  );
}

function Info({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase text-gray-500">{label}</div>
      <div className="mt-1 font-medium">{value || "-"}</div>
    </div>
  );
}
